import { getDataOrNull } from "@msys/common";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Button,
  FormHelperText,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { debounce } from "lodash-es";
import React from "react";
import {
  OnboardingFlowModal_OrganisationDataPrefillQuery,
  useOnboardingFlowModal_OrganisationDataPrefillLazyQuery,
} from "./OnboardingFlowModal.generated.js";
import { FORM_COLORS, TEXT_BUTTON_SX } from "./onboardingFormStyles.js";
import {
  stripWebsitePrefix,
  websiteUrlPrefillValidationSchema,
} from "./websiteUrl.js";

export interface OnboardingWebsitePrefillData {
  website: string;
  prefill: Extract<
    OnboardingFlowModal_OrganisationDataPrefillQuery["organisationDataPrefill"],
    { __typename: "OrganisationDataPrefill" }
  >;
}

export interface OnboardingPrefillResult {
  /** How many fields the lookup filled. */
  count: number;
  website: string;
}

interface Props {
  organisationName: string;
  /** The organisation's current website; shown in the box (without scheme and "www.") on first render. */
  initialWebsite: string;
  /** Called on every edit so the form's website follows the box (without scheme and "www."). */
  onWebsiteChange: (website: string) => void;
  onDataFound: (data: OnboardingWebsitePrefillData) => Promise<void>;
  onLoadingChange: (loading: boolean) => void;
  /** Another step of the lookup (a logo import) is running: "Look up" waits. */
  disabled?: boolean;
  /** The outcome of the last lookup that filled something; shown with an "Undo". */
  result?: OnboardingPrefillResult | null;
  onUndo?: () => void;
}

export const OnboardingFlowWebsitePrefill = ({
  organisationName,
  initialWebsite,
  onWebsiteChange,
  onDataFound,
  onLoadingChange,
  disabled = false,
  result,
  onUndo,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);

  // The box keeps what the user typed; the address it stands for drops a
  // scheme and "www.", so every form of one address gives the same lookup.
  const [entered, setEntered] = React.useState(() =>
    stripWebsitePrefix(initialWebsite)
  );
  const website = stripWebsitePrefix(entered);
  const [websiteValidationError, setWebsiteValidationError] =
    React.useState<Error | null>(null);
  const [searchError, setSearchError] = React.useState<Error | null>(null);
  // Spans the whole click handler (validation, query, awaited onDataFound) —
  // unlike the lazy query's own loading flag, which only covers the network
  // request and would let a second click start an overlapping lookup.
  const [prefillInFlight, setPrefillInFlight] = React.useState(false);
  const [search] = useOnboardingFlowModal_OrganisationDataPrefillLazyQuery();
  // A lookup that settles after the visitor left the business step belongs
  // to a form that no longer exists: its result is dropped.
  const isMountedRef = React.useRef(false);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const validateWebsite = React.useCallback(
    async (value: string): Promise<string | null> => {
      // An empty input is a neutral state, not a validation failure.
      if (!value.trim()) {
        setWebsiteValidationError(null);
        return null;
      }
      try {
        const normalizedWebsite = await websiteUrlPrefillValidationSchema()
          .required()
          .validate(value);
        setWebsiteValidationError(null);
        return normalizedWebsite;
      } catch (error) {
        const validationError =
          error instanceof Error ? error : new Error(String(error));
        // Keep the previous Error instance while the message is unchanged so
        // React bails out of the state update instead of re-rendering on
        // every keystroke that repeats the same error.
        setWebsiteValidationError(previous =>
          previous?.message === validationError.message
            ? previous
            : validationError
        );
        return null;
      }
    },
    []
  );

  const debouncedValidateWebsite = React.useMemo(
    () => debounce(validateWebsite, 300, { leading: false, trailing: true }),
    [validateWebsite]
  );
  React.useEffect(
    () => () => debouncedValidateWebsite.cancel(),
    [debouncedValidateWebsite]
  );

  const handleFillInData = async () => {
    if (prefillInFlight || disabled || !website.trim()) return;

    setPrefillInFlight(true);
    try {
      debouncedValidateWebsite.cancel();
      const normalizedWebsite = await validateWebsite(website);
      if (!normalizedWebsite) {
        return;
      }

      setSearchError(null);
      onLoadingChange(true);
      try {
        const { data, error } = await search({
          variables: {
            input: {
              organisationName,
              website: normalizedWebsite,
            },
          },
        });

        if (!isMountedRef.current) {
          return;
        }
        const prefill = getDataOrNull(data?.organisationDataPrefill);
        if (error || !prefill) {
          setSearchError(
            error ??
              new Error(
                t("Something went wrong. Please try again.", { ns: "Global" })
              )
          );
          return;
        }

        await onDataFound({
          website: normalizedWebsite,
          prefill,
        });
      } finally {
        // An abandoned lookup must not unlock the form while a later visit's
        // own lookup is still running.
        if (isMountedRef.current) {
          onLoadingChange(false);
        }
      }
    } finally {
      setPrefillInFlight(false);
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: "24px" },
        py: "20px",
        border: `1px solid ${FORM_COLORS.tintBorder}`,
        borderRadius: "12px",
        bgcolor: FORM_COLORS.field,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: "10px" }}>
        <AutoAwesomeIcon
          aria-hidden
          sx={{ fontSize: 20, color: FORM_COLORS.boxTitle }}
        />
        <Typography
          component="h2"
          sx={{
            fontSize: 15,
            fontWeight: 500,
            lineHeight: 1.45,
            letterSpacing: "normal",
            color: FORM_COLORS.boxTitle,
          }}
        >
          {t("Fill in from your website", { ns: "OrganisationProfile" })}
        </Typography>
      </Stack>
      <Typography
        sx={{
          mt: "6px",
          fontSize: 13,
          lineHeight: 1.45,
          letterSpacing: "normal",
          color: FORM_COLORS.ink2,
        }}
      >
        {t(
          "We read the imprint of your website and suggest the details below. You decide what to keep. Anything you already typed stays.",
          { ns: "OrganisationProfile" }
        )}
      </Typography>
      <Stack
        direction="row"
        sx={{
          mt: "14px",
          columnGap: "12px",
          rowGap: 1,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <MuiTextField
          hiddenLabel
          placeholder={t("your-business.com", { ns: "OrganisationProfile" })}
          // Editing mid-lookup would let the settled response write a website
          // into the form that no longer matches the visible input.
          disabled={prefillInFlight}
          onChange={event => {
            const typed = event.target.value;
            const address = stripWebsitePrefix(typed);
            setEntered(typed);
            onWebsiteChange(address);
            setSearchError(null);
            debouncedValidateWebsite(address);
          }}
          // The field sits inside the business form, whose default button is
          // "Continue": Enter runs the lookup instead of submitting.
          onKeyDown={event => {
            if (event.key === "Enter") {
              event.preventDefault();
              void handleFillInData();
            }
          }}
          error={Boolean(websiteValidationError)}
          helperText={
            websiteValidationError ? websiteValidationError.message : ""
          }
          value={entered}
          slotProps={{
            htmlInput: {
              "aria-label": t("Website", { ns: "OrganisationProfile" }),
            },
          }}
          sx={{
            flex: "1 1 240px",
            maxWidth: 360,
            minWidth: 0,
            "& .MuiFilledInput-input": {
              fontSize: 16,
              lineHeight: "24px",
              letterSpacing: ".5px",
              color: FORM_COLORS.text,
            },
            "& .MuiFilledInput-root": {
              minHeight: 48,
              bgcolor: "common.white",
              "&:hover:not(.Mui-disabled):not(.Mui-focused)": {
                bgcolor: "common.white",
              },
              "&.Mui-focused": { bgcolor: "common.white" },
            },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AutoAwesomeIcon />}
          onClick={handleFillInData}
          disabled={Boolean(websiteValidationError) || disabled}
          loading={prefillInFlight}
          sx={{
            height: 42,
            px: "22px",
            whiteSpace: "nowrap",
            fontSize: 15,
            lineHeight: "26px",
            fontWeight: 500,
            letterSpacing: ".3px",
            borderRadius: "4px",
          }}
        >
          {t("Look up", { ns: "OrganisationProfile" })}
        </Button>
      </Stack>
      {searchError && (
        <FormHelperText error>{searchError.message}</FormHelperText>
      )}
      {result && (
        <Stack
          direction="row"
          sx={{ mt: "14px", alignItems: "center", gap: "10px" }}
        >
          <CheckCircleIcon
            aria-hidden
            sx={{ fontSize: 20, color: "#1B7A5A" }}
          />
          <Typography
            sx={{
              flex: 1,
              fontSize: 14,
              lineHeight: 1.45,
              letterSpacing: "normal",
              color: FORM_COLORS.ink,
            }}
          >
            {t(
              "{count, plural, one {# detail} other {# details}} found on {website}. Suggested fields are marked. Nothing is saved until you continue.",
              {
                ns: "OrganisationProfile",
                count: result.count,
                website: result.website,
              }
            )}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={onUndo}
            sx={TEXT_BUTTON_SX}
          >
            {t("Undo", { ns: "OrganisationProfile" })}
          </Button>
        </Stack>
      )}
    </Box>
  );
};
